import { useRef, useState } from 'react'

import type { ConnMethod } from '../api/client'
import { publicOnboardingApi } from '../api/publicOnboarding'
import {
  AwsConnectionEditor,
  type AwsConnectionEditorHandle,
} from '../components/aws-import/AwsConnectionEditor'
import { FieldRenderer } from '../components/FieldRenderer'
import { Surface } from '../components/PageShell'
import { COMPANY_PROFILE_SECTION } from '../schema/companyProfileSchema'
import type { FieldDef } from '../schema/intakeSchema'
import { fieldRows } from '../schema/types'

/**
 * PUBLIC customer self-onboarding at /newcustomer — no sign-in. Three steps:
 *
 *   1. Company profile — the same fields (and help bubbles) as the internal
 *      Company profile form, but every field required.
 *   2. AWS access (optional) — the same connection editor the internal AWS
 *      scan uses (Identity Center / per-account keys / organization, with
 *      Test), routed through the token-gated public API. Submitting runs the
 *      scan immediately; the customer can skip instead.
 *   3. "Thank you — we will be contacting you shortly."
 *
 * Step 1's submit creates the company and returns the onboarding token that
 * scopes everything after it to that company. `{slug, token, issuedAt}` is
 * kept in sessionStorage so a reload resumes at step 2 rather than creating a
 * second company; it is cleared on finish and ignored once the 2h token is dead.
 */

/** Genuinely optional on the public form — a company may simply not have an
 *  alias or a sub-industry to give. Everything else is required. */
const OPTIONAL_KEYS = new Set(['aliases', 'sub_industry'])

/** The profile fields — required except the OPTIONAL_KEYS (the public form
 *  has no "recommended" tier). */
const PROFILE_FIELDS: FieldDef[] = COMPANY_PROFILE_SECTION.fields.map((f) => {
  if (OPTIONAL_KEYS.has(f.key)) {
    const { priority: _drop, ...rest } = f
    return rest
  }
  return { ...f, priority: 'required' as const }
})

const REQUIRED_FIELDS: FieldDef[] = PROFILE_FIELDS.filter((f) => !OPTIONAL_KEYS.has(f.key))

function isMissing(field: FieldDef, value: unknown): boolean {
  if (value == null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) {
    if (value.length === 0) return true
    if (field.type.kind === 'contacts-table') {
      // At least one contact we can actually reach.
      return !value.some(
        (c) =>
          typeof c === 'object' &&
          c !== null &&
          String((c as Record<string, unknown>)['name'] ?? '').trim() !== '' &&
          String((c as Record<string, unknown>)['email'] ?? '').trim() !== '',
      )
    }
    return false
  }
  return false
}

const METHOD_OPTIONS: { value: ConnMethod; label: string; blurb: string }[] = [
  {
    value: 'sso',
    label: 'AWS IAM Identity Center (SSO)',
    blurb: 'Sign in through your Identity Center portal — no keys to copy.',
  },
  {
    value: 'per_account',
    label: 'Access keys',
    blurb: 'Paste read-only access keys for each AWS account.',
  },
  {
    value: 'organization',
    label: 'AWS Organization',
    blurb: 'One management-account key; we assume a read-only role in each member account.',
  },
]

/** sessionStorage key for the in-progress onboarding (company slug + its
 *  scoped token). Session — never local — storage: the token is a 2h,
 *  single-company credential that must not outlive the tab. */
export const ONBOARDING_STORAGE_KEY = 'newcustomer.onboarding'

/** The server's onboarding-token lifetime; a stored token older than this is
 *  dead and is discarded instead of resumed. */
export const ONBOARDING_TOKEN_TTL_MS = 2 * 60 * 60 * 1000

interface StoredOnboarding {
  slug: string
  token: string
  issuedAt: number
}

/** The resumable onboarding for this tab, or null. Storage can be blocked
 *  (private mode, disabled site data) — every access is guarded, and a
 *  failure just means "nothing to resume". */
function loadOnboarding(now = Date.now()): StoredOnboarding | null {
  try {
    const raw = window.sessionStorage.getItem(ONBOARDING_STORAGE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<StoredOnboarding>
    if (
      typeof v.slug !== 'string' ||
      typeof v.token !== 'string' ||
      typeof v.issuedAt !== 'number' ||
      !v.slug ||
      !v.token ||
      now - v.issuedAt >= ONBOARDING_TOKEN_TTL_MS
    ) {
      clearOnboarding()
      return null
    }
    return { slug: v.slug, token: v.token, issuedAt: v.issuedAt }
  } catch {
    return null
  }
}

function saveOnboarding(v: StoredOnboarding): void {
  try {
    window.sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(v))
  } catch {
    // Blocked storage: the flow still works, it just can't survive a reload.
  }
}

function clearOnboarding(): void {
  try {
    window.sessionStorage.removeItem(ONBOARDING_STORAGE_KEY)
  } catch {
    // nothing to clear
  }
}

export function NewCustomer() {
  // A reload (or a discarded tab) mid-flow resumes at the AWS step with the
  // same company + token, instead of re-creating the company from scratch.
  const [resumed] = useState(loadOnboarding)
  const [step, setStep] = useState<'profile' | 'aws' | 'done'>(resumed ? 'aws' : 'profile')
  const [draft, setDraft] = useState<Record<string, unknown>>({})
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState(resumed?.token ?? '')
  const [slug, setSlug] = useState(resumed?.slug ?? '')
  const [method, setMethod] = useState<ConnMethod>('sso')
  const [awsReady, setAwsReady] = useState(false)
  const editorRef = useRef<AwsConnectionEditorHandle>(null)

  const submitProfile = async () => {
    const missing = REQUIRED_FIELDS.filter((f) => isMissing(f, draft[f.key])).map((f) => f.label)
    if (!url.trim()) missing.unshift('Website URL')
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.join(', ')}`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await publicOnboardingApi.start(url.trim(), draft)
      setToken(result.token)
      setSlug(result.slug)
      saveOnboarding({ slug: result.slug, token: result.token, issuedAt: Date.now() })
      setStep('aws')
      window.scrollTo(0, 0)
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  /** The flow is over — nothing left to resume, so drop the token. */
  const finish = () => {
    clearOnboarding()
    setStep('done')
  }

  const skipAws = async () => {
    setBusy(true)
    setError(null)
    try {
      await publicOnboardingApi.skipAws(token)
      finish()
    } catch {
      // The company is already created — a skip-marking failure must not
      // strand the customer. Thank them anyway.
      finish()
    } finally {
      setBusy(false)
    }
  }

  const submitAws = async () => {
    setBusy(true)
    setError(null)
    try {
      const saved = await editorRef.current?.save()
      if (!saved) return // the editor shows its own error
      await publicOnboardingApi.runScan(token)
      finish()
      window.scrollTo(0, 0)
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="app-layout">
      <header className="app-header">
        <span className="brand" aria-label="AllCode">
          <img src="/brand/allcode-logo.png" alt="AllCode" />
          <span className="product">Assessments</span>
        </span>
      </header>
      <main className="app-main">
        <div style={{ maxWidth: '46rem', margin: '0 auto', padding: '1rem 0 3rem' }}>
          {step === 'profile' && (
            <Surface testid="newcustomer-profile-step">
              <h1 style={{ marginTop: 0 }}>Tell us about your company</h1>
              <p className="muted" style={{ marginTop: '0.25rem' }}>
                We use this to prepare your assessment. Fields marked * are required.
              </p>
              <div className="field" data-testid="field-website_url" style={{ marginBottom: '1.6rem' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.45rem' }}>
                  Website URL<span style={{ marginLeft: '0.3rem', color: 'var(--accent)', fontWeight: 700 }}>*</span>
                </label>
                <input
                  type="text"
                  value={url}
                  placeholder="https://example.com"
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="newcustomer-url-input"
                />
              </div>
              {fieldRows(PROFILE_FIELDS).map((row) => {
                const rendered = row.map((f) => (
                  <FieldRenderer
                    key={f.key}
                    field={f}
                    value={draft[f.key]}
                    onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                  />
                ))
                return row.length === 2 ? (
                  <div key={row[0]!.key} className="field-grid">{rendered}</div>
                ) : (
                  rendered
                )
              })}
              {error && (
                <div className="error" role="alert" data-testid="newcustomer-error">
                  {error}
                </div>
              )}
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="primary"
                  onClick={submitProfile}
                  disabled={busy}
                  data-testid="newcustomer-profile-continue"
                >
                  {busy ? 'Saving…' : 'Continue'}
                </button>
              </div>
            </Surface>
          )}

          {step === 'aws' && (
            <Surface testid="newcustomer-aws-step">
              <h1 style={{ marginTop: 0 }}>Connect your AWS account</h1>
              <p className="muted" style={{ marginTop: '0.25rem' }}>
                Optional — if you connect read-only AWS access now, we scan your environment
                right away so your assessment starts with a complete picture. You can also
                skip this and we&apos;ll sort out access together later.
              </p>
              <div role="radiogroup" aria-label="Connection method" style={{ display: 'grid', gap: '0.5rem', margin: '1rem 0' }}>
                {METHOD_OPTIONS.map((m) => (
                  <label
                    key={m.value}
                    style={{
                      display: 'flex',
                      gap: '0.6rem',
                      alignItems: 'baseline',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '0.6rem 0.8rem',
                      cursor: 'pointer',
                      background: method === m.value ? 'var(--surface-raised)' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="aws-method"
                      checked={method === m.value}
                      onChange={() => setMethod(m.value)}
                      data-testid={`newcustomer-method-${m.value}`}
                    />
                    <span>
                      <strong>{m.label}</strong>
                      <span className="muted" style={{ display: 'block', fontSize: '0.85rem' }}>{m.blurb}</span>
                    </span>
                  </label>
                ))}
              </div>
              <AwsConnectionEditor
                ref={editorRef}
                slug={slug}
                connection={null}
                method={method}
                onChanged={() => {}}
                onReadyChange={setAwsReady}
                client={{
                  saveAwsConnection: (_slug, payload) => publicOnboardingApi.saveConnection(token, payload),
                  testAwsCredentials: (_slug, accessKey, secretKey, expectedAccountId, sessionToken, awsAccountId) =>
                    publicOnboardingApi.testCredentials(token, accessKey, secretKey, expectedAccountId, sessionToken, awsAccountId),
                }}
                ssoApi={publicOnboardingApi.ssoApi(token)}
              />
              {error && (
                <div className="error" role="alert" data-testid="newcustomer-error">
                  {error}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem' }}>
                <button
                  type="button"
                  className="primary"
                  onClick={submitAws}
                  disabled={busy || !awsReady}
                  title={awsReady ? 'Save the connection and start the scan' : 'Enter credentials above first (or skip)'}
                  data-testid="newcustomer-aws-submit"
                >
                  {busy ? 'Working…' : 'Submit & scan my AWS'}
                </button>
                <button
                  type="button"
                  onClick={skipAws}
                  disabled={busy}
                  data-testid="newcustomer-aws-skip"
                >
                  Skip for now
                </button>
              </div>
            </Surface>
          )}

          {step === 'done' && (
            <Surface testid="newcustomer-done-step" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
              <h1 style={{ marginTop: 0 }}>Thank you.</h1>
              <p style={{ fontSize: '1.05rem' }}>We will be contacting you shortly.</p>
            </Surface>
          )}
        </div>
      </main>
      <footer className="app-footer">© {new Date().getFullYear()} AllCode</footer>
    </div>
  )
}
